import { isAbortError } from '@/lib/errors'
import type { TreeNode } from '@/components/codebook-tree-browser'
import type { LiveUploadStatus } from '@/lib/files/reconcile-upload-status'
import type { ConversationEntry, UploadedFile } from '@/types/chat'
import type { CodebookChange } from '@/types/codebook-delta'
import type { CodebookSchema } from '@/types/codebook-schema'
import type { OdmFormSchema } from '@/types/odm'
import type { ChatSessionSummary, SavedSessionTab } from '@/types/sessions'
import type { GeneratedTestCases, TestCasePlan } from '@/types/test-cases'
import type { PolledUploadResult, UploadStatusRow } from '@/types/uploads'
import { isTerminalUploadStatus } from '@/types/uploads'

// Microservices configuration
// This module runs in the BROWSER (imported by 'use client' components), so
// it needs a host-reachable URL. NEXT_PUBLIC_AI_SERVICE_URL/DATA_SERVICE_URL
// are shared with dozens of Next.js server-side route.ts proxies that run
// inside the Docker network and correctly need the Docker-internal hostname
// (crucible-ai/crucible-data) there — in prod this is moot (that value is
// already a real public HTTPS URL reachable from both sides), but the local
// Docker Compose rig has a real host-vs-container split. The
// NEXT_PUBLIC_BROWSER_* vars are an opt-in override for exactly that case;
// unset (as in prod and any deploy that doesn't set them) falls through to
// the normal var unchanged. Found live 2026-07-27: pointing the shared var
// at localhost to fix this file broke every server-side proxy route instead.
const AI_SERVICE_URL =
  process.env.NEXT_PUBLIC_BROWSER_AI_SERVICE_URL || process.env.NEXT_PUBLIC_AI_SERVICE_URL || 'http://localhost:8000'
const DATA_SERVICE_URL =
  process.env.NEXT_PUBLIC_BROWSER_DATA_SERVICE_URL || process.env.NEXT_PUBLIC_DATA_SERVICE_URL || 'http://localhost:8001'

/**
 * Thrown when an API call needs the Entra id_token (for direct-to-AI
 * calls that bypass the BFF) but the token isn't available. Callers
 * should catch this specifically and render a Reconnect affordance
 * rather than treating it as a generic upload error.
 */
export class AuthRequiredError extends Error {
  readonly isAuthRequired = true
  constructor(message = 'Session expired — re-authenticate to continue') {
    super(message)
    this.name = 'AuthRequiredError'
  }
}

export class APIClient {
  private aiServiceUrl: string
  private dataServiceUrl: string
  private _bearerToken: string | null = null
  // Read userId synchronously from localStorage so requests that fire
  // before the first useEffect (setUserId) still carry the dev header.
  private _userId: string | null =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('crucible_dev_user_id')
      : null

  // In-flight dedup for idempotent GETs. When two callers fire the SAME
  // GET concurrently (e.g. useFileVersions + a stale effect targeting the
  // same endpoint within a render burst), they share the same Promise
  // instead of producing two network round-trips. Entries are removed as
  // soon as the underlying fetch settles, so this is dedup, not caching.
  // Only GETs are deduped — POST/PUT/PATCH/DELETE are state-mutating and
  // must always reach the server.
  private _inflightGets = new Map<string, Promise<unknown>>()

  /**
   * Wrap a GET-only request thunk with in-flight dedup. If a request for
   * `key` is already running, return its Promise. Otherwise run the thunk,
   * cache its Promise under `key`, and evict on settle (success OR failure).
   */
  private dedupGet<T>(key: string, run: () => Promise<T>): Promise<T> {
    const existing = this._inflightGets.get(key) as Promise<T> | undefined
    if (existing) return existing
    const promise = run().finally(() => {
      // Evict regardless of outcome so a failed call doesn't poison the cache.
      if (this._inflightGets.get(key) === promise) {
        this._inflightGets.delete(key)
      }
    })
    this._inflightGets.set(key, promise)
    return promise
  }

  constructor(
    aiServiceUrl: string = AI_SERVICE_URL,
    dataServiceUrl: string = DATA_SERVICE_URL
  ) {
    this.aiServiceUrl = aiServiceUrl
    this.dataServiceUrl = dataServiceUrl
  }

  /** Set the Entra ID token used as Bearer on all direct backend calls. */
  setToken(token: string | null) {
    this._bearerToken = token
  }

  /** Set the user ID — used as a fallback header in local dev when no Bearer token is available. */
  setUserId(userId: string | null) {
    this._userId = userId
    if (typeof window !== 'undefined') {
      if (userId) window.localStorage.setItem('crucible_dev_user_id', userId)
      else window.localStorage.removeItem('crucible_dev_user_id')
    }
  }

  // Getter for base URL (AI service) - used for direct fetch calls
  get baseURL(): string {
    return this.aiServiceUrl
  }

  private authHeaders(): Record<string, string> {
    if (this._bearerToken) {
      return { Authorization: `Bearer ${this._bearerToken}` }
    }
    // Dev fallback: send user ID as a header so the backend can identify
    // the caller without a token (credentials-login has no Entra id_token)
    if (this._userId) {
      return { 'X-Dev-User-Id': this._userId }
    }
    return {}
  }

  /** Route through the Next.js BFF proxy using a relative URL.
   * The proxy injects auth headers from the server-side session, eliminating
   * the client-side token timing dependency.
   *
   * POST/PUT/PATCH bodies are base64-wrapped to prevent Azure WAF from
   * blocking requests whose body contains HTML/XML/SQL-like content
   * (canvas markdown, chat messages, session state).
   */
  private async bffRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const method = options.method?.toUpperCase() ?? 'GET'
    // In-flight dedup for idempotent GETs. Two simultaneous callers of the
    // same endpoint share a single network round-trip instead of producing
    // a stampede. Body-bearing methods are never deduped — they mutate.
    if (method === 'GET') {
      return this.dedupGet(`bff:GET ${endpoint}`, () =>
        this.bffRequestImpl<T>(endpoint, options, method)
      )
    }
    return this.bffRequestImpl<T>(endpoint, options, method)
  }

  /**
   * Same as bffRequest, but never gzip+base64-wraps the body. Only
   * chat-v3/sessions/file-versions routes decode that WAF-bypass envelope
   * (see payload-codec.ts) — every other BFF proxy (compli, marq, qualio,
   * codebooks, test-cases) does a dumb pass-through and expects raw JSON.
   * Sending a wrapped body to one of those silently breaks it: the backend
   * receives `{payload_gz64: "..."}` instead of the real Pydantic body
   * (found while migrating codebooks' import endpoints off direct-to-AI
   * calls, 2026-07-29).
   */
  private async bffRequestPlain<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const method = options.method?.toUpperCase() ?? 'GET'
    if (method === 'GET') {
      return this.dedupGet(`bff:GET ${endpoint}`, () =>
        this.bffRequestImpl<T>(endpoint, options, method, false)
      )
    }
    return this.bffRequestImpl<T>(endpoint, options, method, false)
  }

  private async bffRequestImpl<T>(
    endpoint: string,
    options: RequestInit,
    method: string,
    compress = true
  ): Promise<T> {
    let body = options.body

    // Compress + base64-wrap bodies to bypass WAF body inspection.
    // Compression obscures content patterns that trigger WAF managed rules
    // and reduces payload size for large session saves.
    if (compress && body && typeof body === 'string' && ['POST', 'PUT', 'PATCH'].includes(method)) {
      const raw = new TextEncoder().encode(body)
      const cs = new CompressionStream('gzip')
      const writer = cs.writable.getWriter()
      writer.write(raw)
      writer.close()
      const compressed = await new Response(cs.readable).arrayBuffer()
      const bytes = new Uint8Array(compressed)
      let binary = ''
      for (const b of bytes) binary += String.fromCharCode(b)
      body = JSON.stringify({ payload_gz64: btoa(binary) })
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30_000)

    try {
      const response = await fetch(endpoint, {
        ...options,
        body,
        headers,
        signal: controller.signal,
      })

      if (!response.ok) {
        const body = await response.text()
        // Attach the HTTP status so callers can distinguish transient auth
        // failures (401/403 during session-transition windows) from real
        // errors without brittle message-string parsing.
        const error = new Error(`API Error (bff): ${response.status} - ${body}`) as Error & { status?: number }
        error.status = response.status
        throw error
      }

      return response.json()
    } catch (err) {
      if (isAbortError(err)) {
        throw new Error(`Request timed out after 30s (bff: ${endpoint})`)
      }
      throw err
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private async request<T>(
    endpoint: string,
    serviceType: 'ai' | 'data' = 'ai',
    options: RequestInit = {}
  ): Promise<T> {
    const method = options.method?.toUpperCase() ?? 'GET'
    // See bffRequest — same dedup story for direct AI/data GETs.
    if (method === 'GET') {
      return this.dedupGet(`${serviceType}:GET ${endpoint}`, () =>
        this.requestImpl<T>(endpoint, serviceType, options)
      )
    }
    return this.requestImpl<T>(endpoint, serviceType, options)
  }

  private async requestImpl<T>(
    endpoint: string,
    serviceType: 'ai' | 'data',
    options: RequestInit
  ): Promise<T> {
    const baseUrl = serviceType === 'ai' ? this.aiServiceUrl : this.dataServiceUrl
    const url = `${baseUrl}${endpoint}`

    const headers = {
      'Content-Type': 'application/json',
      ...this.authHeaders(),
      ...options.headers,
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30_000)

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(`API Error (${serviceType}): ${response.status} - ${error}`)
      }

      return response.json()
    } catch (err) {
      if (isAbortError(err)) {
        throw new Error(`Request timed out after 30s (${serviceType}: ${endpoint})`)
      }
      throw err
    } finally {
      clearTimeout(timeoutId)
    }
  }

  // Chat session management (Data Service)
  async createChatSession(sessionId?: string, studyId?: string, title?: string) {
    return this.request<{ id: string; study_id: string | null; title: string; created_at: string }>('/api/chat/sessions', 'data', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        study_id: studyId,
        title: title || 'Chat Session'
      }),
    })
  }

  async getChatSession(sessionId: string) {
    return this.request(`/api/chat/sessions/${sessionId}`, 'data')
  }

  async listChatSessions(studyId?: string) {
    const endpoint = studyId
      ? `/api/chat/sessions?study_id=${studyId}`
      : '/api/chat/sessions'
    // The data service returns a BARE ARRAY here (list_chat_sessions in
    // crucible-data-service/main.py), not `{sessions: [...]}` — which is
    // what this used to declare. Nothing calls this method, so the wrong
    // shape never produced a runtime failure.
    return this.request<ChatSessionSummary[]>(endpoint, 'data')
  }

  // Chat message persistence (AI Service with blob storage fallback)
  // This now uses the AI Service directly, which persists to blob storage
  // when Data Service is unavailable (local development mode)
  //
  // TODO: Consolidate session storage architecture
  // Currently we have dual storage (Blob + PostgreSQL) with fallback logic which creates complexity:
  // - Canvas/tabs: sessions/session_*.json in Blob Storage
  // - Messages: messages/session_*.json in Blob Storage (primary) OR chat_messages table in PostgreSQL (fallback)
  //
  // The dual storage redundancy is intentional for now - it safeguards against session restoration
  // failures if either storage system is unavailable. This is a temporary development stage concern.
  //
  // Once we implement per-user session persistence and compartmentalization, we can consolidate.
  // Currently everyone shares one authentication credential, so we can't attribute sessions to
  // specific users without inspecting actual message content.
  //
  // Future consolidation options:
  // Option A: Blob Storage primary - combine messages + tabs into single session file
  // Option B: PostgreSQL primary - add session_canvas table, use Blob only for large binary files
  //
  // See: docs/sessions/ for session restoration investigation notes
  async saveChatMessage(
    sessionId: string,
    role: 'user' | 'assistant',
    content: string,
    /**
     * Opaque per-turn JSON. `ChatMessageCreate.message_metadata` is
     * `Optional[Dict[str, Any]]` (backend/app/api/routes/sessions.py) and
     * the server never inspects a key — it stores the object as-is and
     * mirrors it to `chat_messages`. The two writers in use-chat.ts send
     * different key sets (user turn: chat_attempt_id/assistant_message_id;
     * assistant turn: tool_results/usage/tool_stats/elapsed_ms/tools_used/
     * model/provider/turn_steps), so there is no single shape to name here.
     * `PersistedMessageMetadata` in @/types/chat is the narrower READ view.
     */
    metadata?: Record<string, unknown> | null,
    messageId?: string,
    /** IDs of files attached to this message — typically populated for
     *  the user turn when the chat references uploaded documents. The
     *  backend creates one row in file_messages per id in the same
     *  transaction as the chat_messages mirror. */
    fileIds?: string[],
  ) {
    // Route through BFF proxy — the proxy injects X-Crucible-User-Id from
    // the server-side session, so this works even when the Entra id_token
    // is unavailable (e.g. after container restart).
    return this.bffRequest<{ id: string; session_id: string; role: string; content: string; created_at: string }>('/api/sessions/messages', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        role,
        content,
        message_id: messageId,
        message_metadata: metadata || null,
        file_ids: fileIds && fileIds.length > 0 ? fileIds : undefined,
      }),
    })
  }

  async submitMessageFeedback(messageId: string, sessionId: string, feedback: 'thumbs_up' | 'thumbs_down') {
    return this.bffRequest<{ id: string; feedback: string }>(`/api/sessions/messages/${messageId}/feedback`, {
      method: 'PATCH',
      body: JSON.stringify({ session_id: sessionId, feedback }),
    })
  }

  async getChatMessages(sessionId: string) {
    try {
      const messages = await this.bffRequest<import('@/types/chat').PersistedChatMessage[]>(`/api/sessions/messages/${sessionId}`)
      return messages ?? []
    } catch (err) {
      const status = (err as Error & { status?: number })?.status
      if (status === 403) {
        const forbidden = new Error('Session not accessible') as Error & { status: number }
        forbidden.status = 403
        throw forbidden
      }
      console.warn('[APIClient] Message load failed:', err)
      return []
    }
  }

  // File upload — single unified path via Next.js BFF using a base64-JSON
  // envelope. This dodges the Front Door WAF: multipart bodies containing
  // XML-like content (DOCX is a zip of XML files) trip WAF managed rules,
  // but the WAF only inspects the request body it sees — an opaque JSON
  // with a base64 string never matches XML/HTML/SQL patterns.
  //
  // Trade-offs:
  //   - +33% upload size (base64 inflation). Acceptable for typical doc sizes;
  //     could add gzip later but most clinical-study files (PDF, DOCX, XLSX)
  //     are already compressed internally.
  //   - Reads the entire file into memory twice (File → ArrayBuffer → base64).
  //     Mitigation: FileReader.readAsDataURL is the fastest native path.
  //
  // Auth: the BFF reads the session cookie server-side and injects
  // X-Crucible-User-Id, so this path survives AI-service restarts and
  // honors impersonation cleanly — no client-side token dependency.
  async uploadFile(file: File, sessionId?: string) {
    // FileReader.readAsDataURL is the fastest native bytes→base64 path.
    // The result is a "data:<mime>;base64,<payload>" URL; strip the prefix.
    const file_b64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const commaIdx = result.indexOf(',')
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result)
      }
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
      reader.readAsDataURL(file)
    })

    const response = await fetch('/api/files/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        file_b64,
        session_id: sessionId,
      }),
    })

    if (response.status === 401) {
      throw new AuthRequiredError('Session expired — re-authenticate to upload')
    }
    if (!response.ok) {
      throw new Error(`File upload error: ${response.status}`)
    }
    return response.json()
  }

  /**
   * Valet Key upload — browser puts file bytes DIRECTLY to Azure Blob via a
   * short-lived SAS URL minted by the backend. App Service is out of the data
   * path entirely; the WAF never sees the bytes.
   *
   * Three steps:
   *   1. POST /api/files/init-upload → { sas_url, blob_path, file_id }
   *   2. PUT bytes to sas_url (direct browser→blob)
   *   3. POST /api/files/complete-upload → triggers extraction + AI Files API
   *
   * `onProgress` receives 0..1 during the blob PUT — use XHR (not fetch),
   * since fetch still doesn't expose request upload progress reliably.
   */
  async uploadFileViaValetKey(
    file: File,
    sessionId?: string,
    onProgress?: (fraction: number) => void,
    onPollingStart?: () => void,
  ) {
    // Step 1 — mint SAS
    const initRes = await fetch('/api/files/init-upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size: file.size,
        session_id: sessionId,
      }),
    })
    if (initRes.status === 401) {
      throw new AuthRequiredError('Session expired — re-authenticate to upload')
    }
    if (!initRes.ok) {
      throw new Error(`init-upload error: ${initRes.status} ${await initRes.text()}`)
    }
    const { file_id, blob_path, sas_url } = await initRes.json() as {
      file_id: string
      blob_path: string
      sas_url: string
    }

    // Step 2 — PUT bytes directly to blob via SAS. XHR for progress events.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', sas_url, true)
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')
      // Content-Type must match what we bound into the SAS at mint time,
      // or Azure rejects with 403 AuthenticationFailed.
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded / e.total)
        }
      }
      // Allow at least 60s + 10s per MB. A 19MB file gets ~250s; a 50MB
      // file gets ~560s. The XHR aborts when the deadline really has
      // passed (no live network activity), not when an arbitrary
      // baseline runs out. The 2026-06-04 19MB JnJ-deck upload exceeded
      // the previous 60s baseline and produced a blob-present-but-DB-
      // stuck artifact because ontimeout fired mid-upload.
      xhr.timeout = Math.max(60_000, 60_000 + file.size / 1024 * 10)
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(1)
          resolve()
        } else {
          reject(new Error(`blob PUT failed: ${xhr.status} ${xhr.responseText.slice(0, 300)}`))
        }
      }
      xhr.onerror = () => reject(new Error('blob PUT network error'))
      xhr.ontimeout = () => reject(new Error('Upload timed out — try again'))
      xhr.send(file)
    })

    // Step 3 — complete: backend downloads blob, extracts, hands to AI Files API.
    // Wrap in AbortController with a 30s timeout — without this, an AFD/WAF hang
    // would leave the calling code awaiting indefinitely and the DB row stuck
    // at 'processing' (the 2026-06-04 19MB JnJ-deck failure mode).
    const completeAbort = new AbortController()
    const completeTimer = setTimeout(() => completeAbort.abort(), 30_000)
    let completeRes: Response
    try {
      completeRes = await fetch('/api/files/complete-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: completeAbort.signal,
        body: JSON.stringify({
          file_id,
          blob_path,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size: file.size,
          session_id: sessionId,
        }),
      })
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        throw new Error(
          `complete-upload timed out after 30s — backend or AFD edge is hung. ` +
          `file_id=${file_id} blob_path=${blob_path}. The blob is uploaded; ` +
          `triage the DB row to recover.`
        )
      }
      throw err
    } finally {
      clearTimeout(completeTimer)
    }
    if (completeRes.status === 401) {
      throw new AuthRequiredError('Session expired — re-authenticate to upload')
    }
    if (completeRes.status === 202) {
      onPollingStart?.()
      return await this._pollUploadStatus(file_id, file.name)
    }
    if (!completeRes.ok) {
      throw new Error(`complete-upload error: ${completeRes.status} ${await completeRes.text()}`)
    }
    return completeRes.json()
  }

  private async _pollUploadStatus(
    fileId: string,
    filename: string,
    timeoutMs = 300_000,
  ): Promise<PolledUploadResult> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 2_000))
      try {
        const res = await fetch(`/api/files/upload-status/${fileId}`, { credentials: 'same-origin' })
        if (res.status === 404) continue
        if (!res.ok) continue
        const data: UploadStatusRow = await res.json()
        // Every terminal status resolves the poll, not just 'ready'/'error'.
        // 'stored' is what the backend settles on when local text extraction
        // failed but the AI Files API upload succeeded — a usable file Claude
        // reads natively. Treating it as transient meant the pill spun for the
        // full timeout below and then reported a FALSE 'error'.
        if (isTerminalUploadStatus(data.status)) {
          return { files: [{ ...data, filename }], db_persisted: true }
        }
      } catch { /* transient network — keep polling */ }
    }
    // Timed out. Return a TERMINAL 'error', never 'processing' — a file left at
    // 'processing' keeps uploadInProgress=true forever, which permanently
    // disables the Send button with no way to recover (the pill looks like it's
    // still working, so the user doesn't know to remove it). 'error' is
    // terminal, shows the "remove it and retry" affordance, and the file is
    // removable so Send unblocks. (live report, 2026-06-17.)
    return {
      files: [{
        id: fileId,
        filename,
        status: 'error',
        failure_reason: 'processing_timeout',
      }],
      db_persisted: false,
    }
  }

  async getFile(fileId: string) {
    return this.request(`/api/files/${fileId}`, 'ai')
  }

  /**
   * Live upload status from the files table (the durable source of truth).
   * Returns null when no row exists (404) — the upload never completed.
   * Used to re-derive transient statuses on session hydrate instead of
   * trusting the snapshot frozen into the session blob.
   *
   * Typed as `LiveUploadStatus` — the consumer-side view in
   * @/lib/files/reconcile-upload-status — rather than the full
   * `UploadStatusRow`. The row carries more (blob_path, file_size,
   * extracted_content), but `LiveUploadStatus` is what the one caller
   * reconciles against, and it also covers the camelCase `errorMessage` /
   * `failureReason` variants the reconciler tolerates.
   */
  async getUploadStatus(fileId: string): Promise<LiveUploadStatus | null> {
    const res = await fetch(`/api/files/upload-status/${fileId}`, { credentials: 'same-origin' })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`upload-status error: ${res.status}`)
    return res.json()
  }

  async listFiles() {
    return this.request('/api/files/list', 'ai')
  }

  async saveFileVersion(params: {
    studyId: string, sessionId: string, filename: string,
    content: string, contentType: string, parentVersion?: string,
    version?: string,
    sourceBlobPath?: string, originalFileSize?: number,
  }): Promise<{ file_id: string; version: string; file_size: number; blob_path: string }> {
    const body: Record<string, unknown> = {
      study_id: params.studyId,
      session_id: params.sessionId,
      filename: params.filename,
      content: params.content,
      content_type: params.contentType,
      parent_version: params.parentVersion ?? '0.0',
    }
    if (params.version) body.version = params.version
    if (params.sourceBlobPath) body.source_blob_path = params.sourceBlobPath
    if (params.originalFileSize) body.original_file_size = params.originalFileSize
    // Relative path, NOT this.request(..., 'ai', ...) -- that resolves
    // against this.aiServiceUrl (NEXT_PUBLIC_AI_SERVICE_URL), which on the
    // local rig is the Docker-internal hostname http://crucible-ai:8000.
    // NEXT_PUBLIC_* vars are inlined into the browser bundle at build time,
    // so the BROWSER (not the Next.js server) tried to resolve that hostname
    // directly and failed with net::ERR_NAME_NOT_RESOLVED -- confirmed live
    // 2026-07-24, the manual Save button silently never worked on the local
    // rig. The relative path goes through app/api/files/versions/route.ts,
    // which runs server-side (where the Docker-internal hostname IS
    // resolvable) and forwards to the real backend -- the exact same proxy
    // use-content-tabs.ts's auto-snapshot already uses successfully.
    const response = await fetch('/api/files/versions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`API Error (files/versions): ${response.status} - ${error}`)
    }
    return response.json()
  }

  /**
   * Download original binary file from blob storage.
   *
   * Relative path through the BFF, NOT `${this.aiServiceUrl}` with
   * `authHeaders()`. A direct call sends the real signed-in user's id_token,
   * so while impersonating it authenticated as the ADMIN — and the backend's
   * IDOR guard (`blob_path.startswith(f"{user['id']}/")`) then 404s every
   * blob the impersonated user owns. Observed as "Failed to load PDF" on
   * exactly their uploads, because uploading goes through the BFF and is
   * stored under the effective user's id. Impersonation is resolved from a
   * cookie server-side in `resolveEffectiveUser`, so only a server-side hop
   * can see it — the same reasoning `uploadFile` records above.
   *
   * Encoding is per-segment, preserving slashes, matching `getFileContent`:
   * the backend route is `{blob_path:path}`, and encoding the whole string
   * turns its separators into %2F.
   */
  async downloadBinaryFile(blobPath: string): Promise<Blob> {
    const encodedPath = blobPath.split('/').map(encodeURIComponent).join('/')
    const response = await fetch(`/api/files/download/${encodedPath}`)
    if (!response.ok) throw new Error(`Download failed: ${response.status}`)
    return response.blob()
  }

  async listFileVersions(studyId: string, sessionId: string, options?: { all?: boolean }) {
    const query = options?.all ? '?all=true' : ''
    return this.bffRequest<{ versions: import('@/types/chat').FileVersion[] }>(
      `/api/files/versions/${encodeURIComponent(studyId)}/${encodeURIComponent(sessionId)}${query}`
    )
  }

  async getFileVersionHistory(studyId: string, sessionId: string, filename: string) {
    return this.bffRequest<{ versions: import('@/types/chat').FileVersion[] }>(
      `/api/files/versions/${encodeURIComponent(studyId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(filename)}`
    )
  }

  async getFileContent(blobPath: string) {
    // Encode each path segment (em-dash / spaces / non-ASCII filenames) while
    // preserving slashes — the backend route is {blob_path:path}. Raw
    // interpolation produced a malformed URL → 400 before the handler ran.
    const encodedPath = blobPath.split('/').map(encodeURIComponent).join('/')
    return this.bffRequest<{ content: string; blob_path: string }>(
      `/api/files/content/${encodedPath}`,
    )
  }

  async renameFileVersion(sessionId: string, filename: string, newFilename: string) {
    return this.bffRequest<{ renamed: number; new_filename: string }>(
      `/api/files/versions/${encodeURIComponent(sessionId)}/${encodeURIComponent(filename)}/rename`,
      { method: 'PUT', body: JSON.stringify({ new_filename: newFilename }) }
    )
  }

  async deleteFileVersion(sessionId: string, filename: string) {
    return this.bffRequest<{ deleted: number }>(
      `/api/files/versions/${encodeURIComponent(sessionId)}/${encodeURIComponent(filename)}`,
      { method: 'DELETE' }
    )
  }

  // User management (Data Service)
  async getCurrentUser() {
    return this.request('/api/users/me', 'data')
  }

  async createUser(userData: { email: string; full_name: string; azure_object_id?: string }) {
    return this.request('/api/users/create', 'data', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  // Study management (Data Service - Persistence)
  //
  // CAUTION — this whole block is unused by the app (the live path is
  // `listStudiesMerged`/`saveToCloud` in @/lib/user-study-registry, which
  // fetches /api/studies directly) and it does not match the service it
  // names. Verified against crucible-data-service/main.py, 2026-09-21:
  //   - list/create/update/delete exist; there is NO GET /api/studies/{id}.
  //   - the service has no `study_data` column at all. `_study_payload_to_columns`
  //     reads `code`/`title`/`phase`/`status`/`sessionId` plus a `ui` metadata
  //     bag; `name`, `description` and `study_data` are read by nothing, and
  //     a body without `title` violates the NOT NULL on the `name` column.
  // Types below describe what the service actually returns/reads. They are
  // left in place rather than deleted because tests/lib/api-client.test.ts
  // exercises these methods.

  /**
   * `GET /api/studies` returns a BARE ARRAY of `_study_to_ui(...)` rows —
   * not `{studies: [...]}`, which is what this used to declare. The row is
   * the same shape as `StoredUserStudy` in @/lib/user-study-registry.
   */
  async listStudies() {
    return this.request<{
      id: string
      slug: string | null
      code: string
      title: string
      phase: string | null
      status: string
      currentVersionId: string | null
      /** Echoed back verbatim from whatever the client POSTed; unvalidated. */
      versions: unknown[]
      forms: number
      items: number
      lastActiveAt: string | null
      stepIdx: number
      createdAt: string
      sessionId: string | null
      cloud: boolean
    }[]>('/api/studies', 'data')
  }

  /** No such route on the data service — this 404s. See the block comment. */
  async getStudy(studyId: string) {
    return this.request<{ id: string; name: string; study_data: unknown }>(`/api/studies/${studyId}`, 'data')
  }

  async createStudy(studyData: { name: string; description: string; study_data: unknown; status?: string }) {
    return this.request('/api/studies', 'data', {
      method: 'POST',
      body: JSON.stringify(studyData),
    })
  }

  async updateStudy(studyId: string, updateData: { name?: string; description?: string; study_data?: unknown; status?: string }) {
    return this.request(`/api/studies/${studyId}`, 'data', {
      method: 'PUT',
      body: JSON.stringify(updateData),
    })
  }

  async deleteStudy(studyId: string) {
    return this.request(`/api/studies/${studyId}`, 'data', {
      method: 'DELETE',
    })
  }

  // Study generation (AI Service - ODM XML Generation)
  /**
   * Dead: `@app.post("/api/studies/generate")` is commented out in
   * crucible-ai-service/main.py (superseded by the chat-based flow), so
   * there is no request model to mirror and this 404s.
   */
  async generateStudy(studyRequest: unknown) {
    return this.request('/api/studies/generate', 'ai', {
      method: 'POST',
      body: JSON.stringify(studyRequest),
    })
  }

  async exportStudy(studyId: string, format: 'xml' | 'json' = 'xml') {
    // Get study data from Data Service
    const study = await this.getStudy(studyId)
    
    // Generate export via AI Service
    const response = await fetch(`${this.aiServiceUrl}/api/studies/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
              },
      body: JSON.stringify({ study_data: study.study_data, format }),
    })

    if (!response.ok) {
      throw new Error(`Export error: ${response.status}`)
    }

    return response.blob()
  }

  // Session content persistence — routed through Next.js BFF proxy for reliable auth
  async saveSessionContent(
    sessionId: string,
    tabs: SavedSessionTab[],
    activeTabId: string | null = null,
    uploadedFiles: UploadedFile[] = [],
    baseEtag: string | null = null
  ) {
    // Strip extracted_content before persisting. The backend stores file
    // metadata only (the extracted text lives in the files table + the
    // Anthropic Files API), so sending it here is dead weight that inflates
    // the request — even gzip-wrapped — past the AFD/WAF body limit and gets
    // the save 403'd, truncating the restored file list (Finding A). The
    // stored blob is byte-identical with or without this; only the request
    // shrinks. Restore re-fetches content by file_id when needed.
    const slimFiles = (uploadedFiles || []).map((f: UploadedFile) => {
      // Runtime guard predating the type — kept because the array can come
      // straight from a restored session blob, which nothing revalidates.
      if (!f || typeof f !== 'object') return f
      const { extracted_content: _extracted_content, ...meta } = f
      return meta
    })
    return this.bffRequest<{ success: boolean; message: string; blob_path?: string; etag?: string | null }>(
      '/api/sessions/save',
      {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          tabs,
          active_tab_id: activeTabId,
          uploaded_files: slimFiles,
          base_etag: baseEtag,
        }),
      }
    )
  }

  async loadSessionContent(sessionId: string) {
    return this.bffRequest(`/api/sessions/${sessionId}`)
  }

  async deleteSessionContent(sessionId: string) {
    return this.bffRequest(`/api/sessions/${sessionId}`, { method: 'DELETE' })
  }

  // Conversation index (per-user conversation history)
  async listConversations(userId: string, studyId?: string) {
    const qs = new URLSearchParams({ user_id: userId })
    if (studyId) qs.set('study_id', studyId)
    // The server's own ConversationEntry (backend/app/api/routes/sessions.py)
    // is `model_dump()`ed, so it also carries `title_locked` and
    // `title_source`, and sends `study_id`/`version_id` as explicit nulls
    // rather than omitting them. The frontend interface is the narrower
    // view both consumers already use.
    return this.bffRequest<{ user_id: string; sessions: ConversationEntry[] }>(
      `/api/sessions/?${qs.toString()}`
    )
  }

  // Canvas tabs aggregated across all of the user's sessions. Drives the
  // "Canvases" section in Concierge's recent-chats popover. Backed by a 60s
  // TTL cache on the server; the frontend hook adds its own dedupe +
  // revalidate-on-focus layer. Capped server-side to 50 items, newest first.
  async getCanvasLibrary() {
    return this.bffRequest<{
      items: Array<{
        target_tool: string
        section_id: string
        title: string
        session_id: string
        session_title: string
        updated_at: string
      }>
      computed_at: string
    }>(`/api/sessions/canvas-library`)
  }

  // messageCount/hasCanvas are optional: when omitted they're sent as undefined
  // (JSON.stringify drops them) so the backend treats them as "do not change" —
  // a rename must not zero the message count. titleLocked=true marks a manual
  // rename so the message-derived auto-titler can't overwrite it.
  async registerConversation(
    userId: string,
    sessionId: string,
    title?: string,
    messageCount?: number,
    hasCanvas?: boolean,
    titleLocked?: boolean,
    studyId?: string,
    versionId?: string,
    // Which writer produced `title`. The server ranks these
    // (placeholder < derived < generated < user) and refuses a write that
    // would replace a higher tier — 'derived' may only fill a placeholder.
    // Omitting it is treated as 'derived', the conservative default, so a
    // caller that does not declare cannot overwrite a real title.
    titleSource?: 'derived' | 'generated' | 'user',
  ) {
    return this.bffRequest<{
      success: boolean
      session_id: string
      entry: {
        session_id: string
        title: string
        created_at: string
        updated_at: string
        message_count: number
        has_canvas: boolean
        title_locked: boolean
        study_id?: string
        version_id?: string
      }
      changed: boolean
    }>(
      '/api/sessions/register', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          session_id: sessionId,
          title: title || undefined,
          title_locked: titleLocked || undefined,
          title_source: titleSource || undefined,
          message_count: messageCount,
          has_canvas: hasCanvas,
          study_id: studyId || undefined,
          version_id: versionId || undefined,
        }),
      }
    )
  }

  async claimSession(userId: string, userEmail: string, sessionId: string, studyId?: string) {
    return this.bffRequest<{ success: boolean; session_id: string; indexed: boolean; db_updated: boolean }>(
      '/api/sessions/claim', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          user_email: userEmail,
          session_id: sessionId,
          study_id: studyId || undefined,
        }),
      }
    )
  }

  async deleteConversation(userId: string, sessionId: string) {
    return this.bffRequest<{ success: boolean }>(
      `/api/sessions/${sessionId}/index?user_id=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    )
  }
  // ── Test Case Generator (via BFF — see bffRequest's docstring; these
  //    used to call `this.request()` direct-to-AI with a client-held Entra
  //    bearer, which raced NextAuth's id_token hydration and 401'd with no
  //    retry, live on crucible.3phalliance.com, 2026-07-29) ─────────────

  async getTestCaseStudies() {
    return this.bffRequest<{ studies: { OID: string; StudyName: string }[] }>(
      '/api/test-cases/studies'
    )
  }

  async getTestCaseForms(studyOid: string) {
    return this.bffRequest<{ forms: { form_oid: string; form_name: string }[] }>(
      `/api/test-cases/studies/${encodeURIComponent(studyOid)}/forms`
    )
  }

  async getFormSchema(formOid: string, studyOid: string) {
    return this.bffRequest<OdmFormSchema>(
      `/api/test-cases/forms/${encodeURIComponent(formOid)}/schema?study_oid=${encodeURIComponent(studyOid)}`
    )
  }

  // ── Codebook Browser (SharePoint Graph API + Blob fallback) ──────

  async listCodebookTree(sharePointAccessToken?: string) {
    return this.bffRequest<{ source: string; tree: TreeNode[] }>('/api/test-cases/codebook-tree', {
      headers: sharePointAccessToken ? { 'X-SharePoint-Access-Token': sharePointAccessToken } : {},
    })
  }

  // Same payload as listCodebookTree — the refresh route only invalidates
  // the server-side tree cache first, then calls the identical builder.
  async refreshCodebookTree(sharePointAccessToken?: string) {
    return this.bffRequest<{ source: string; tree: TreeNode[] }>('/api/test-cases/codebook-tree/refresh', {
      headers: sharePointAccessToken ? { 'X-SharePoint-Access-Token': sharePointAccessToken } : {},
    })
  }

  /** @deprecated Use listCodebookTree instead */
  async listCodebookFiles() {
    return this.bffRequest<{
      files: { id: string; name: string; size: number; last_modified: string }[]
    }>('/api/test-cases/codebook-files')
  }

  async getCodebookSheets(fileId: string, source?: string, sharePointAccessToken?: string) {
    const params = new URLSearchParams()
    if (source) params.set('source', source)
    const qs = params.toString()
    return this.bffRequest<{ sheets: string[] }>(
      `/api/test-cases/codebook-files/${fileId.split('/').map(encodeURIComponent).join('/')}/sheets${qs ? '?' + qs : ''}`,
      {
        headers: sharePointAccessToken ? { 'X-SharePoint-Access-Token': sharePointAccessToken } : {},
      }
    )
  }

  async parseCodebookFromBlob(
    fileId: string,
    sheetName?: string,
    source?: string,
    sharePointAccessToken?: string,
    codebookType?: string,
  ) {
    const formData = new FormData()
    formData.append('file_id', fileId)
    if (sheetName) formData.append('sheet_name', sheetName)
    if (source) formData.append('source', source)
    if (codebookType) formData.append('codebook_type', codebookType)
    const response = await fetch('/api/test-cases/parse-codebook-from-sharepoint', {
      method: 'POST',
      headers: sharePointAccessToken ? { 'X-SharePoint-Access-Token': sharePointAccessToken } : {},
      body: formData,
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.detail || `Parse error: ${response.status}`)
    }
    return response.json()
  }

  async generateManualTestCases(
    codebookSchema: CodebookSchema,
    formNames?: string[],
    codebookType?: string,
  ) {
    return this.bffRequestPlain<GeneratedTestCases>('/api/test-cases/generate-manual', {
        method: 'POST',
        body: JSON.stringify({
          codebook_schema: codebookSchema,
          form_names: formNames,
          codebook_type: codebookType,
        }),
      }
    )
  }

  async generateQAScenarios(payload: {
    study_oid: string
    form_oid?: string | null
    codebook_schema?: CodebookSchema
    /**
     * NOTE: `QAScenariosRequest` (backend/app/api/routes/test_cases.py) does
     * not declare this field, and Pydantic v2 drops unknown keys by default —
     * so it is sent and silently discarded. Kept only so the existing caller
     * keeps compiling; the Layer-1 context it is meant to carry never reaches
     * the scenario agent. `unknown` rather than a named shape for the same
     * reason: naming one would imply the server reads it.
     */
    layer1_results?: unknown
    /** Never put on the wire below — see the body built in this method. */
    selected_form_name?: string
    custom_instructions?: string
  }): Promise<Response> {
    // Connect-only timeout — the BFF streams SSE, so headers return once the
    // upstream StreamingResponse opens. Aborting the whole LLM turn at 15s
    // was killing Layer 2 with a status-0 network error.
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60_000)

    let response: Response
    try {
      response = await fetch('/api/test-cases/qa/scenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          study_oid: payload.study_oid,
          form_oid: payload.form_oid ?? undefined,
          codebook_schema: payload.codebook_schema,
          layer1_results: payload.layer1_results,
          custom_instructions: payload.custom_instructions,
        }),
        signal: controller.signal,
      })
    } catch (err) {
      clearTimeout(timeoutId)
      if (isAbortError(err)) {
        throw new Error(`Request timed out after 60s waiting for scenario stream (ai: /api/test-cases/qa/scenarios)`)
      }
      throw err
    }

    clearTimeout(timeoutId)

    if (!response.ok) throw new Error(`Scenario generation error: ${response.status}`)
    return response
  }

  async generateQAGaps(payload: {
    study_oid?: string
    form_oid?: string | null
    // All three are `dict[str, Any]` on QAGapsRequest — the gap agent reads
    // them reflectively, so there is no declared schema to mirror. The shapes
    // this app sends are built in src/contexts/test-case-context.tsx.
    stride_schema?: Record<string, unknown>
    layer1_results: Record<string, unknown>
    layer2_results: Record<string, unknown>
  }): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60_000)

    let response: Response
    try {
      response = await fetch('/api/test-cases/qa/gaps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      })
    } catch (err) {
      clearTimeout(timeoutId)
      if (isAbortError(err)) {
        throw new Error(`Request timed out after 60s waiting for gap stream (ai: /api/test-cases/qa/gaps)`)
      }
      throw err
    }

    clearTimeout(timeoutId)

    if (!response.ok) throw new Error(`Gap analysis error: ${response.status}`)
    return response
  }

  /**
   * `testCases` is `unknown` rather than `TestCasePlan` because the route
   * binds `test_cases: dict` with no validation (ExportADORequest in
   * backend/app/api/routes/test_cases.py) and `export_ado_excel` then walks
   * `categories[].test_cases[].steps[]` reflectively — a caller can and does
   * hand it something that is not a plan at all, and narrowing here would
   * only move the failure from the server to the compiler for one caller
   * while leaving the wire contract unchecked either way.
   */
  async exportADOExcel(testCases: unknown, assignedTo?: string, areaPath?: string): Promise<Blob> {
    const response = await fetch('/api/test-cases/export-ado', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ test_cases: testCases, assigned_to: assignedTo, area_path: areaPath }),
    })
    if (!response.ok) throw new Error(`ADO export error: ${response.status}`)
    return response.blob()
  }

  async pushToADO(params: {
    mode: 'new' | 'existing'
    // mode=new
    planName?: string
    areaPath?: string
    iterationPath?: string
    assignedTo?: string
    // mode=existing
    existingPlanId?: number
    existingSuiteId?: number
    // always
    tags: string[]
    testCases: TestCasePlan
    project?: string
    adoAccessToken?: string
    studyFolder?: string
  }): Promise<Response> {
    const response = await fetch('/api/test-cases/push-to-ado', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: params.mode,
        plan_name: params.planName,
        area_path: params.areaPath ?? '',
        iteration_path: params.iterationPath ?? '',
        assigned_to: params.assignedTo ?? process.env.NEXT_PUBLIC_ADO_DEFAULT_ASSIGNEE ?? '',
        existing_plan_id: params.existingPlanId,
        existing_suite_id: params.existingSuiteId,
        tags: params.tags,
        test_cases: params.testCases,
        project: params.project || '3PHCloud',
        ado_access_token: params.adoAccessToken,
        study_folder: params.studyFolder,
      }),
    })
    if (!response.ok && response.status !== 200) throw new Error(`Push to ADO error: ${response.status}`)
    return response
  }

  async uploadCodebook(
    file: File,
    folder?: string,
  ): Promise<{ blob_path: string; size: number; filename: string }> {
    const formData = new FormData()
    formData.append('file', file)
    if (folder) formData.append('folder', folder)
    const response = await fetch('/api/test-cases/codebook-upload', {
      method: 'POST',
      body: formData,
    })
    if (!response.ok) {
      const detail = await response.json().catch(() => ({ detail: 'Upload failed' }))
      throw new Error(detail.detail || `Upload error: ${response.status}`)
    }
    return response.json()
  }

  async importXlsxTestPlan(file: File): Promise<GeneratedTestCases> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch('/api/test-cases/import-xlsx', {
      method: 'POST',
      body: formData,
    })
    if (!response.ok) {
      const detail = await response.json().catch(() => ({ detail: 'Import failed' }))
      throw new Error(detail.detail || `Import error: ${response.status}`)
    }
    return response.json()
  }

  async parseCodebook(file: File, tabName?: string) {
    const formData = new FormData()
    formData.append('file', file)
    if (tabName) formData.append('tab_name', tabName)

    const response = await fetch('/api/test-cases/parse-codebook', {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Codebook parse error: ${response.status}`)
    }

    return response.json() as Promise<{ filename: string; changes: CodebookChange[]; change_count: number }>
  }

  async generateTestCases(request: {
    study_oid: string
    form_oid: string
    item_group_oids?: string[]
    codebook_delta?: CodebookChange[]
    custom_instructions?: string
  }) {
    return this.bffRequestPlain<{
      categories: {
        name: string
        test_cases: {
          id: string
          title: string
          steps: { step: number; action: string; field: string; input: string; expected: string }[]
        }[]
      }[]
    }>('/api/test-cases/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  /**
   * Excel export of a test-case plan.
   *
   * Hand-rolled fetch rather than `this.request` because the response is a
   * Blob, not JSON. It must still carry `authHeaders()`: the data service's
   * `/api/test-cases/export-excel` was anonymous and is authenticated as of
   * AB#23780, and this was the one call in the test-case family that sent no
   * credential — its four siblings (saveTestCaseRun, getTestCaseSuite,
   * getTestCaseRun, deleteTestCaseRun) already go through `this.request`,
   * which attaches them.
   */
  async exportTestCasesExcel(result: TestCasePlan, formOid: string, studyName?: string, formName?: string): Promise<Blob> {
    const response = await fetch('/api/test-cases/export-excel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ result, form_oid: formOid, study_name: studyName, form_name: formName }),
    })
    if (!response.ok) throw new Error(`Export error: ${response.status}`)
    return response.blob()
  }
  // ── Test Case Persistence (Data Service) ─────────────────────────

  async saveTestCaseRun(data: {
    study_oid: string
    study_name: string
    form_oid: string
    form_name: string
    /**
     * Whatever the run produced. Deliberately `unknown`, not a plan: the
     * column is `result_json: Mapped[str] = mapped_column(Text)` and the
     * data-service request model is a bare `dict`, and three structurally
     * different payloads are written through here — a Layer-1 `TestCasePlan`,
     * a Layer-2 `{scenarios, reviews, ...}` bundle, and a Layer-3
     * `{gaps, dispositions, ...}` bundle — discriminated only by the
     * `val:scenarios:` / `val:gaps:` prefix on `form_oid`. Readers narrow.
     */
    result_json: unknown
    edits_json?: Record<string, string>
    item_group_oids?: string[]
    codebook_context?: {
      file_id: string
      source: string
      sheets: string[]
      sheet: string
      selected_forms: string[]
      codebook_type?: 'l3phc' | 'anvil'
      codebook_schema: CodebookSchema
    }
  }) {
    return this.bffRequestPlain<{ id: string; suite_id: string; form_oid: string; updated_at: string }>('/api/test-cases/runs', {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  async getTestCaseSuite(studyOid: string) {
    return this.bffRequestPlain<{
      suite: { id: string; study_oid: string; study_name: string; created_at: string } | null
      runs: { id: string; form_oid: string; form_name: string; item_group_oids: string[] | null; updated_at: string }[]
    }>(`/api/test-cases/suites/${encodeURIComponent(studyOid)}`)
  }

  async getTestCaseRun(suiteId: string, formOid: string) {
    return this.bffRequestPlain<{
      id: string
      suite_id: string
      form_oid: string
      form_name: string
      /** See saveTestCaseRun — one of three payload shapes; narrow at the use site. */
      result_json: unknown
      edits_json: Record<string, string>
      item_group_oids: string[] | null
      codebook_context: {
        file_id: string
        source: string
        sheets: string[]
        sheet: string
        selected_forms: string[]
        codebook_type?: 'l3phc' | 'anvil'
        codebook_schema: CodebookSchema
      } | null
      created_at: string
      updated_at: string
    }>(`/api/test-cases/runs/${encodeURIComponent(suiteId)}/${encodeURIComponent(formOid)}`)
  }

  async deleteTestCaseRun(runId: string) {
    return this.bffRequestPlain<{ message: string }>(`/api/test-cases/runs/${encodeURIComponent(runId)}`, { method: 'DELETE' }
    )
  }

  // Codebook Object API (via BFF — codebooks.py requires surface:norma on
  // every endpoint, and these used to call this.request() direct-to-AI with
  // a client-held Entra bearer, which raced NextAuth's id_token hydration
  // and 401'd with no retry, live on crucible.3phalliance.com, 2026-07-29)

  async inferCodebookMetadata(fileId: string, source = 'blob', sheetName?: string) {
    return this.bffRequestPlain<{
      protocol_name: string | null
      codebook_name: string | null
      version_number: string | null
      version_label: string | null
      filename: string
      sheets_available: string[]
      suggested_sheet: string | null
    }>('/api/codebooks/infer-metadata', {
      method: 'POST',
      body: JSON.stringify({ file_id: fileId, source, sheet_name: sheetName }),
    })
  }

  async importCodebook(data: {
    file_id: string
    source?: string
    sheet_name: string
    protocol_name: string
    codebook_name?: string
    version_number: string
    version_label?: string
    change_summary?: string
    protocol_number?: string
    sponsor?: string
    phase?: string
    therapeutic_area?: string
    created_by?: string
  }) {
    return this.bffRequestPlain<{
      protocol_id: string
      codebook_id: string
      version_id: string
      protocol_name: string
      version_number: string
      version_label: string | null
      protocol_created: boolean
      stats: { arms: number; events: number; forms: number; fields: number; options: number; skipped: number }
    }>('/api/codebooks/import', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async importCodebookStream(
    data: Parameters<typeof this.importCodebook>[0],
    signal?: AbortSignal
  ): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15_000)

    // If caller provides a signal (for user cancellation), forward aborts
    if (signal) {
      signal.addEventListener('abort', () => controller.abort(), { once: true })
    }

    let response: Response
    try {
      response = await fetch('/api/codebooks/import/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        signal: controller.signal,
      })
    } catch (err) {
      clearTimeout(timeoutId)
      if (isAbortError(err)) {
        if (signal?.aborted) {
          throw new Error('Import cancelled')
        }
        throw new Error(`Request timed out after 15s (bff: /api/codebooks/import/stream)`)
      }
      throw err
    }

    clearTimeout(timeoutId)

    return response
  }

  async listProtocols() {
    return this.bffRequest<{
      protocols: {
        id: string
        name: string
        protocol_number: string | null
        sponsor: string | null
        phase: string | null
        therapeutic_area: string | null
        status: string
        codebooks: {
          id: string
          name: string
          current_version_id: string | null
          latest_version: {
            id: string
            version_number: string
            version_label: string | null
            status: string
            created_at: string
          } | null
        }[]
      }[]
      total: number
    }>('/api/codebooks/protocols')
  }

  async getCodebookVersion(versionId: string) {
    return this.bffRequest<{
      id: string
      codebook_id: string
      version_number: string
      version_label: string | null
      status: string
      change_summary: string | null
      imported_from: string | null
      created_at: string
      arms: { id: string; name: string }[]
      events: { id: string; name: string }[]
      forms: { id: string; name: string; field_count: number; event_id: string | null }[]
      summary: { total_arms: number; total_events: number; total_forms: number; total_fields: number }
    }>(`/api/codebooks/versions/${versionId}`)
  }

  async publishCodebookVersion(versionId: string) {
    return this.bffRequest<{ id: string; status: string; published_at: string }>(
      `/api/codebooks/versions/${versionId}/publish`, { method: 'POST' }
    )
  }

  async discardCodebookVersion(versionId: string) {
    return this.bffRequest<{ id: string; discarded: boolean }>(
      `/api/codebooks/versions/${versionId}`, { method: 'DELETE' }
    )
  }

  async getProtocolAllVersions(protocolId: string) {
    return this.bffRequest<{
      protocol_id: string
      versions: {
        id: string
        version_number: string
        version_label: string | null
        status: string
        codebook_id: string
        codebook_name: string
        created_at: string
      }[]
    }>(`/api/codebooks/protocols/${protocolId}/all-versions`)
  }

  async getCodebookVersions(codebookId: string) {
    return this.bffRequest<{
      codebook_id: string
      codebook_name: string
      versions: {
        id: string
        version_number: string
        version_label: string | null
        status: string
        created_at: string
      }[]
    }>(`/api/codebooks/${codebookId}/versions`)
  }

  async compareVersions(v1Id: string, v2Id: string) {
    return this.bffRequest<{
      v1: { id: string; version_number: string; version_label: string | null }
      v2: { id: string; version_number: string; version_label: string | null }
      summary: { added: number; removed: number; modified: number; unchanged: number }
      forms: {
        form_name: string
        changes: {
          varname: string
          change_type: 'added' | 'removed' | 'modified' | 'unchanged'
          label: string | null
          data_type: string | null
          changes: { column: string; old: string | null; new: string | null }[]
        }[]
      }[]
    }>(`/api/codebooks/versions/${v1Id}/diff/${v2Id}`)
  }

  async getFormFields(formId: string) {
    return this.bffRequest<{
      form_id: string
      form_name: string
      total: number
      fields: {
        id: string
        varname: string
        label: string | null
        data_type: string | null
        mandatory: string | null
        arm_raw: string | null
        is_multiselect_parent: boolean
        selection_list: string | null
        error_min: string | null
        error_max: string | null
        warning_min: string | null
        warning_max: string | null
        rules: string | null
        notes: string | null
        options: { id: string; sub_varname: string; label: string }[]
      }[]
    }>(`/api/codebooks/forms/${formId}/fields`)
  }
}

// Global API client instance
export const apiClient = new APIClient()// pipeline var rebuild 1775458282
